import { create } from "zustand";
import { apiConfig } from "@config/api.config";
import { abortActiveRequests } from "@shared/api/client";
import {
  authApi,
  type AuthMe,
  type UpdateProfilePayload,
} from "@shared/api/auth";

interface AuthState {
  me: AuthMe | null;
  loading: boolean;
  loaded: boolean;
  ensureLoaded: () => Promise<boolean>;
  refresh: () => Promise<void>;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  selectEmisor: (emisorId: number) => Promise<void>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<void>;
  hasPermission: (perm: string) => boolean;
  hasRole: (role: string) => boolean;
}

function resolveAssetUrl(url?: string | null): string | null {
  if (!url) {
    return null;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return new URL(url, apiConfig.sanctumBaseURL).toString();
}

function normalizeMe(me: AuthMe): AuthMe {
  const normalizeRole = (r: string): string =>
    r === "super_admin" ? "super-admin" : r;

  // Preservamos los roles reales del backend (Spatie): son la fuente de los
  // permisos efectivos. La columna legacy `user.rol` es solo el rol principal
  // de visualización.
  const backendRoles =
    me.roles && me.roles.length > 0 ? me.roles : [me.user.rol ?? "empleado"];

  // Normaliza super_admin → super-admin y conserva el alias legacy para que
  // `hasRole("super_admin")` y `hasRole("super-admin")` funcionen igual.
  const roles = Array.from(
    new Set(
      backendRoles.flatMap((r) => {
        const role = normalizeRole(r);
        return role === "super-admin" ? ["super-admin", "super_admin"] : [role];
      }),
    ),
  );

  const rol = normalizeRole(me.user.rol ?? roles[0]);

  return {
    ...me,
    user: {
      ...me.user,
      rol,
      foto_url: resolveAssetUrl(me.user.foto_url),
    },
    roles,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  me: null,
  loading: false,
  loaded: false,

  async ensureLoaded() {
    if (get().loaded) return get().me !== null;

    set({ loading: true });

    try {
      const me = normalizeMe(await authApi.me());

      set({
        me,
        loaded: true,
        loading: false,
      });

      return true;
    } catch {
      set({
        me: null,
        loaded: true,
        loading: false,
      });

      return false;
    }
  },

  async refresh() {
    const me = normalizeMe(await authApi.me());

    set({ me });
  },

  async login(email, password, remember = false) {
    const me = normalizeMe(
      await authApi.login(email, password, remember),
    );

    set({
      me,
      loaded: true,
    });
  },

  async logout() {
    abortActiveRequests("logout");

    set({
      me: null,
      loaded: true,
      loading: false,
    });

    void authApi.logout().catch(() => {
      // El cierre local de sesión ya se ha completado. Si el backend tarda o
      // devuelve timeout, no debe bloquear login ni dejar al usuario atrapado.
    });
  },

  async selectEmisor(emisorId) {
    const me = normalizeMe(
      await authApi.selectEmisor(emisorId),
    );

    set({ me });
  },

  async updateProfile(payload) {
    const me = normalizeMe(await authApi.updateProfile(payload));

    set({ me });
  },

  hasPermission(perm) {
    return get().me?.permissions.includes(perm) ?? false;
  },

  hasRole(role) {
    const me = get().me;
    const normalizedRole = role === "super_admin" ? "super-admin" : role;

    return (
      me?.user?.rol === normalizedRole ||
      me?.roles.includes(normalizedRole) ||
      me?.roles.includes(role) ||
      false
    );
  },
}));
